import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { api, formatEUR } from "@/lib/api";

const PLACILO_LABELS = {
  predracun: "Plačilo po predračunu",
  povzetje: "Plačilo po povzetju",
  kartica: "Plačilo s kartico ob prevzemu",
};

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="order-not-found">
        <h1 className="font-display text-2xl font-bold text-[#1A1D1A]">Naročila ni bilo mogoče najti</h1>
        <Link to="/" className="mt-4 inline-block text-[#B45309] font-semibold hover:underline">Na domačo stran</Link>
      </div>
    );
  }

  if (!order) {
    return <p className="text-center text-stone-500 py-24" data-testid="order-loading">Nalagam naročilo …</p>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid="order-confirmation-page">
      <div className="text-center mb-10">
        <CheckCircle2 className="w-16 h-16 text-[#2D4030] mx-auto mb-4" />
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A]">
          Naročilo uspešno oddano!
        </h1>
        <p className="mt-3 text-stone-600">
          Hvala za vaše naročilo. Potrditev smo poslali na <strong>{order.customer.email}</strong>.
        </p>
        <p className="mt-4 inline-block bg-[#F3EFE6] rounded-full px-5 py-2 font-tech font-bold text-[#B45309]" data-testid="order-number">
          Št. naročila: {order.id}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8 space-y-6" data-testid="order-details">
        <div>
          <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-4">Postavke</h2>
          <ul className="space-y-3">
            {order.items.map((i, idx) => (
              <li key={idx} className="flex justify-between gap-4 text-sm">
                <div>
                  <p className="font-semibold text-[#1A1D1A]">{i.name}</p>
                  {i.meta && <p className="text-xs text-stone-500">{i.meta}</p>}
                  <p className="text-xs text-stone-500">{i.qty} × {formatEUR(i.price)}</p>
                </div>
                <span className="font-tech font-semibold shrink-0">{formatEUR(i.line_total)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-5 border-t border-[#E4E0D7] space-y-2 text-sm">
          <div className="flex justify-between text-stone-600">
            <span>Vmesna vsota</span><span className="font-tech">{formatEUR(order.subtotal)}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Dostava</span>
            <span className="font-tech">{order.shipping === 0 ? "Brezplačno" : formatEUR(order.shipping)}</span>
          </div>
          <div className="flex justify-between font-display font-bold text-lg pt-3 border-t border-[#E4E0D7]">
            <span>Skupaj z DDV</span>
            <span className="font-tech text-[#B45309]" data-testid="order-total">{formatEUR(order.total)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-5 border-t border-[#E4E0D7] text-sm">
          <div>
            <h3 className="font-semibold text-[#1A1D1A] mb-1.5">Naslov</h3>
            <p className="text-stone-600">
              {order.customer.ime} {order.customer.priimek}<br />
              {order.customer.naslov}<br />
              {order.customer.posta} {order.customer.kraj}
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#1A1D1A] mb-1.5">Dostava in plačilo</h3>
            <p className="text-stone-600">
              {order.dostava === "prevzem" ? "Osebni prevzem — Industrijska cesta 12, Ljubljana" : "Dostava na dom"}<br />
              {PLACILO_LABELS[order.placilo] || order.placilo}
            </p>
          </div>
        </div>

        {order.placilo === "predracun" && (
          <p className="text-xs text-stone-500 bg-[#F3EFE6] rounded-lg p-3">
            Predračun z navodili za plačilo boste prejeli na e-pošto v roku 24 ur.
          </p>
        )}
      </div>

      <div className="text-center mt-10">
        <Link
          to="/trgovina"
          data-testid="order-continue-shopping-btn"
          className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors"
        >
          Nadaljujte z nakupom <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
